import { Location as RouterLocation } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Location, Countries, Provinces } from '../../model/country';
import {
    postalCodeFormatHint,
    postalCodeValidator,
} from '../../validators/postal-code-validator';
import {
    STREET_LINE_SIGN_HINT,
    requiredStreetLineValidator,
    streetLineValidator,
    trimStreetLine,
} from '../../validators/street-line-validator';
import {
    FormBuilder,
    Validators,
    FormGroup,
    FormControl,
    AbstractControl,
} from '@angular/forms';
import {
    AccountAddress,
    AccountAddressRequest,
    Country,
    CreateAccountAddress201Response,
    UsersService,
} from '@viewbid/ts-angular-client';
import { ActivatedRoute } from '@angular/router';
import { BiddingUtils } from '@/vb-utils/bidding.util';
import { first } from 'rxjs';
import { VbSnackbarService } from '@viewbid/ui';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'vb-user-addresses-form',
    templateUrl: './user-addresses-form.component.html',
    styleUrls: ['./user-addresses-form.component.scss'],
})
export class UserAddressesFormComponent implements OnInit {
    countries = Countries;
    provinces = Provinces;
    allCountries = Country;
    selectedCountry = Country.Ca;
    filteredProvinces: Location[] = [];
    provinceLabel = 'Province';
    postalCodeLabel = 'Postal Code';
    postalCodePlaceholder = 'A1A1A1';
    public addressForm!: FormGroup;
    public formSubmitted = false;
    /** Save confirm (vb-dialog) — replaces the legacy ngx-bootstrap
     *  ConfirmationDialogComponent for this flow. */
    confirmOpen: boolean = false;
    public currentUserId!: string;
    isFormValid = false;
    isLoading: boolean = false;
    isEditMode: boolean = false;
    addressData!: AccountAddress;

    constructor(
        private _formBuilder: FormBuilder,
        private biddingUtils: BiddingUtils,
        private usersService: UsersService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private route: ActivatedRoute,
        private location: RouterLocation,
    ) {}

    ngOnInit(): void {
        this.addressForm = this._formBuilder.group({
            name: new FormControl(null, [
                Validators.required,
                Validators.maxLength(50),
            ]),
            address1: new FormControl(null, [
                requiredStreetLineValidator(),
                Validators.maxLength(45),
                streetLineValidator(),
            ]),
            address2: new FormControl(null, [
                Validators.maxLength(45),
                streetLineValidator(),
            ]),
            city: new FormControl(null, [
                Validators.required,
                Validators.maxLength(45),
            ]),
            province: new FormControl(null, [Validators.required]),
            postalCode: new FormControl(null, [
                Validators.required,
                Validators.maxLength(10),
                postalCodeValidator(),
            ]),
            country: new FormControl(null, [
                Validators.required,
                Validators.maxLength(3),
            ]),
            buzzer: new FormControl(null, [Validators.maxLength(10)]),
            isDefault: new FormControl(false),
        });

        if (this.country) {
            this.country.valueChanges.subscribe((value: Country) => {
                this.applyCountry(value);
            });
        }

        this.biddingUtils
            .getCurrentUserId()
            .pipe(first())
            .subscribe((userId: string) => {
                this.currentUserId = userId;
            });

        this.addressForm.valueChanges.subscribe(() => {
            this.isFormValid = this.validate();
        });

        this.route.params.subscribe({
            next: (data: any) => {
                if (data['addressId']) {
                    this.isEditMode = true; // Set mode to Edit
                    this.loadFormForEdit(data['addressId']); // Load the item data for editing
                }
            },
        });
    }

    goToListAddress(): void {
        this.location.back();
    }

    // Open confirmation modal (the Save button is disabled while invalid;
    // touching everything surfaces errors if it's ever reached invalid).
    openConfirmationModal(): void {
        if (!this.addressForm.valid) {
            this.markAllAsTouched();
            return;
        }
        this.confirmOpen = true;
    }

    /** Same translated copy the legacy confirmation dialog showed. */
    get confirmTitle(): string {
        return this.translate.instant(
            this.isEditMode
                ? 'Users.updateAddressActionTitle'
                : 'Users.addAddressActionTitle',
        );
    }

    get confirmMessage(): string {
        return this.translate.instant(
            this.isEditMode
                ? 'Users.updateAddressConfirmationMessage'
                : 'Users.addAddressConfirmationMessage',
        );
    }

    onSaveConfirmed(): void {
        this.confirmOpen = false;
        this.submitForm();
    }

    submitForm(): void {
        this.formSubmitted = true;
        if (this.addressForm.valid) {
            const userAddressData: AccountAddressRequest = {
                address1: trimStreetLine(this.addressForm.value.address1),
                address2: trimStreetLine(this.addressForm.value.address2),
                city: this.addressForm.value.city,
                province: this.addressForm.value.province,
                country: this.addressForm.value.country,
                postalCode: this.addressForm.value.postalCode,
                buzzer: this.addressForm.value.buzzer,
                name: this.addressForm.value.name,
                isDefault: this.addressForm.value.isDefault as boolean,
            };

            if (this.isEditMode) {
                this.usersService
                    .updateUserAccountAddressById(
                        this.addressData.id as string,
                        userAddressData,
                    )
                    .subscribe({
                        next: () => {
                            const successMessage: string =
                                this.translate.instant(
                                    userAddressData.isDefault
                                        ? 'Users.updateAddressDefaultSuccessful'
                                        : 'Users.updateAddressSuccessful',
                                );

                            this.snackbarService.success(successMessage);
                            this.goToListAddress();
                            this.resetFormValue();
                            this.formSubmitted = false;
                        },
                        error: () => {
                            this.snackbarService.error(
                                this.translate.instant(
                                    'Users.updateAddressFailed',
                                ),
                            );
                        },
                    });
            } else {
                this.usersService
                    .createUserAccountAddress(userAddressData)
                    .subscribe({
                        next: () => {
                            const successMessage: string =
                                this.translate.instant(
                                    userAddressData.isDefault
                                        ? 'Users.addAddressDefaultSuccessful'
                                        : 'Users.addAddressSuccessful',
                                );

                            this.snackbarService.success(successMessage);
                            this.goToListAddress();
                            this.resetFormValue();
                            this.formSubmitted = false;
                        },
                        error: () => {
                            this.snackbarService.error(
                                this.translate.instant(
                                    'Users.addAddressFailed',
                                ),
                            );
                        },
                    });
            }
        }
    }

    get name(): AbstractControl<any, any> | null {
        return this.addressForm.get('name');
    }
    get address1(): AbstractControl<any, any> | null {
        return this.addressForm.get('address1');
    }
    get address2(): AbstractControl<any, any> | null {
        return this.addressForm.get('address2');
    }
    get city(): AbstractControl<any, any> | null {
        return this.addressForm.get('city');
    }
    get province(): AbstractControl<any, any> | null {
        return this.addressForm.get('province');
    }
    get postalCode(): AbstractControl<any, any> | null {
        return this.addressForm.get('postalCode');
    }
    get country(): AbstractControl<any, any> | null {
        return this.addressForm.get('country');
    }
    get buzzer(): AbstractControl<any, any> | null {
        return this.addressForm.get('buzzer');
    }
    get isDefault(): AbstractControl<any, any> | null {
        return this.addressForm.get('isDefault');
    }

    onCountrySelected(): void {
        this.addressForm.controls['postalCode'].setValue('');
        this.addressForm.controls['province'].setValue('');
    }

    applyCountry(country: Country): void {
        this.selectedCountry = country;
        const isUnitedStates: boolean = country === Country.Us;
        this.provinceLabel = isUnitedStates ? 'State' : 'Province';
        this.postalCodeLabel = isUnitedStates ? 'ZIP' : 'Postal Code';
        this.postalCodePlaceholder = isUnitedStates ? '12345' : 'A1A1A1';
        this.sortedProvinces();
        this.postalCode?.updateValueAndValidity();
    }

    sortedProvinces(): Location[] {
        return (this.filteredProvinces = this.provinces
            .filter(
                (province: Location) =>
                    province.country === this.selectedCountry,
            )
            .sort((input: Location, filter: Location) =>
                input.value.localeCompare(filter.value),
            ));
    }

    markAllAsTouched(): void {
        this.addressForm.markAllAsTouched();
    }

    validate(): boolean {
        this.addressForm.markAllAsTouched();
        return this.addressForm.valid;
    }

    resetFormValue(): void {
        this.addressForm.reset({
            name: null,
            address1: null,
            address2: null,
            city: null,
            province: null,
            postalCode: null,
            country: null,
            buzzer: null,
            isDefault: null,
        });
    }

    loadFormForEdit(id: string): void {
        this.usersService.getUserAccountAddressById(id).subscribe({
            next: (response: CreateAccountAddress201Response) => {
                this.addressData = response.data;
                this.addressForm.patchValue({
                    name: this.addressData.name,
                    address1: this.addressData.address1,
                    address2: this.addressData.address2,
                    buzzer: this.addressData.buzzer,
                    city: this.addressData.city,
                    province: this.addressData.province,
                    postalCode: this.addressData.postalCode,
                    country: this.addressData.country,
                    isDefault: this.addressData.isDefault,
                });
            },
            error: () => {
                const failureMessage: string = this.translate.instant(
                    'Users.fetchAddressFailed',
                );
                this.snackbarService.error(failureMessage);
                this.goToListAddress();
            },
        });
    }

    getHelperText(field: string): string | null {
        const control: AbstractControl<any, any> | null =
            this.addressForm.get(field);
        // If no control found, return empty
        if (!control) {
            return null;
        }

        const requiredValidation: boolean =
            control.hasError('required') &&
            (control?.touched || this.formSubmitted);
        const maxLengthValidation: boolean =
            control.hasError('maxlength') &&
            (control?.touched || this.formSubmitted);
        const postalCodeValidation: boolean =
            control.hasError('postalCode') &&
            (control?.touched || this.formSubmitted);
        const leadingSignValidation: boolean =
            control.hasError('leadingSign') &&
            (control?.touched || this.formSubmitted);
        switch (field) {
            case 'name':
                if (requiredValidation) {
                    return 'Name is required';
                }
                if (maxLengthValidation) {
                    return `Name can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'address1':
                if (requiredValidation) {
                    return 'Address is required';
                }
                if (maxLengthValidation) {
                    return `Address can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (leadingSignValidation) {
                    return STREET_LINE_SIGN_HINT;
                }
                break;
            case 'address2':
                if (maxLengthValidation) {
                    return `Address can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (leadingSignValidation) {
                    return STREET_LINE_SIGN_HINT;
                }
                break;
            case 'city':
                if (requiredValidation) {
                    return 'City is required';
                }
                if (maxLengthValidation) {
                    return `City can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'province':
                if (requiredValidation) {
                    return 'Province is requiredt';
                }
                if (maxLengthValidation) {
                    return `Province can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'postalCode':
                if (requiredValidation) {
                    return 'Postal Code is required';
                }
                if (maxLengthValidation) {
                    return `Postal Code can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                if (postalCodeValidation) {
                    return postalCodeFormatHint(this.selectedCountry);
                }
                break;
            case 'country':
                if (requiredValidation) {
                    return 'Country is required';
                }
                if (maxLengthValidation) {
                    return `Country can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            case 'buzzer':
                if (maxLengthValidation) {
                    return `Buzzer can be max ${control.errors?.['maxlength'].requiredLength} characters long`;
                }
                break;
            default:
                return null;
        }

        return null;
    }
}
